import * as stylex from "@stylexjs/stylex";
import { useMutation, useQuery, useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from '@tanstack/react-router';
import {
  object as vObject,
  string as vString,
  optional as vOptional,
  literal as vLiteral,
  parse as vParse,
  union as vUnion
} from 'valibot';

import { ArticleListItem } from "@/components/article-list-item.component";
import { ArticleList } from "@/components/article-list.component";
import { PublisherListItem } from "@/components/publisher-list-item.component";
import { PublisherList } from "@/components/publisher-list.component";
import { useInfiniteScrollTrigger } from "@/hooks/use-infinite-scroll-trigger.hook";
import {
  Button,
  EmptyState,
  Heading,
  Paragraph,
  ProgressIndicator,
  Tab,
  TabList,
  Tabs,
  View
} from '@/libs/serenity-ui';
import { articleMutations, articleQueries } from "@/resources/article/article.api";
import { publicationQueries } from "@/resources/publication/publication.api";
import { userQueries } from "@/resources/user/user.api";

export const Route = createFileRoute('/_app/_list/search/$tab')({
  component: RouteComponent,
  validateSearch: vObject({
    q: vOptional(vString()),
  }),
  params: {
    parse: (params) => vParse(vObject({
      tab: vUnion([vLiteral('articles'), vLiteral('publishers')]),
    }), params),
  },
  staticData: {
    contextBarComponent: ContextBarComponent,
  }
});

function RouteComponent() {
  const params = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const saveArticleMutation = useMutation(articleMutations.save({ search: { q: search.q ?? '' } }));
  const unsaveArticleMutation = useMutation(articleMutations.unsave({ search: { q: search.q ?? '' } }));
  const excludeArticleMutation = useMutation(articleMutations.exclude({ search: { q: search.q ?? '' } }));
  const undoExcludeArticleMutation = useMutation(articleMutations.removeExclude({ search: { q: search.q ?? '' } }));

  const userInterestsQuery = useQuery(userQueries.interests);
  const searchArticlesQuery = useSuspenseInfiniteQuery(articleQueries.search({ q: search.q ?? '' }));
  const searchPublicationsQuery = useSuspenseInfiniteQuery(publicationQueries.search({ q: search.q ?? '' }));

  const {
    fetchNextPage,
    isLoading,
    isFetching,
    hasNextPage
  } = params.tab === 'articles' ? searchArticlesQuery : searchPublicationsQuery;

  useInfiniteScrollTrigger({ isFetching, hasNextPage, fetchNextPage });

  if (params.tab === 'articles') {
    const articles = searchArticlesQuery.data?.pages?.flatMap((page) => page.articles) ?? [];

    return isLoading || articles.length > 0 ?
      <>
        <ArticleList aria-label="Search results" items={articles}>
          {({ key, index, style, ref, ...item }) =>
            <ArticleListItem
              authors={item.authors}
              data-index={index}
              id={item.id}
              isExcluded={item.excluded}
              isLast={index === articles.length - 1}
              isSaved={item.saved}
              isSeen={item.seen}
              key={key}
              publicationDate={item.publication_date}
              publicationId={item.publication_id}
              publicationName={item.publication_name}
              ref={ref}
              style={style}
              title={item.title}
              url={item.url}
              onExcludePress={() => excludeArticleMutation.mutate({ article_id: item.id })}
              onSavePress={() => saveArticleMutation.mutate({ article_id: item.id })}
              onUndoExcludePress={() => undoExcludeArticleMutation.mutate({ article_id: item.id })}
              onUnsavePress={() => unsaveArticleMutation.mutate({ article_id: item.id })}
            />}
        </ArticleList>
        {!isLoading && hasNextPage && <ProgressIndicator size={24} style={progressIndicatorStyles.base} />}
      </> :
      <EmptyState style={emptyStateStyles.base}>
        <Heading>No results</Heading>
        <Paragraph>Try another search term or choose from the suggestions below</Paragraph>
        <View style={emptyStateStyles.suggestions}>
          {userInterestsQuery.data?.map(interest =>
            <Button
              isRounded
              key={interest.id}
              size={40}
              variant="quaternary"
              onPress={() => navigate({
                replace: true, // important to redirect to the correct page when deleting the search term
                params: { tab: params.tab },
                search: (prev) => ({ ...prev, q: interest.name })
              })}>
              {interest.name} ({interest.tree_number})
            </Button>)}
        </View>
      </EmptyState>;
  }

  const publications = searchPublicationsQuery.data?.pages?.flatMap((page) => page.publications) ?? [];

  return isLoading || publications.length > 0 ?
    <>
      <PublisherList aria-label="Search results" items={publications}>
        {({ key, index, style, ref, ...item }) =>
          <PublisherListItem
            articleCount={item.article_count}
            data-index={index}
            id={item.id}
            isLast={index === publications.length - 1}
            key={key}
            name={item.name}
            ref={ref}
            style={style}
            url={item.domain}
          />}
      </PublisherList>
      {!isLoading && hasNextPage && <ProgressIndicator size={24} style={progressIndicatorStyles.base} />}
    </> :
    <EmptyState style={emptyStateStyles.base}>
      <Heading>No results</Heading>
      <Paragraph>Try a different search term</Paragraph>
    </EmptyState>;
}

function ContextBarComponent() {
  const navigate = Route.useNavigate();
  const params = Route.useParams();

  return (
    <Tabs
      selectedKey={params.tab}
      style={tabsStyles.base}
      onSelectionChange={(key) => navigate({
        replace: true, // important to redirect to the correct page when deleting the search term
        params: { tab: (key.toString() as 'articles' | 'publishers') },
        search: (prev) => prev
      })}>
      <TabList style={tabsStyles.list}>
        <Tab id="articles" size={48}>Articles</Tab>
        <Tab id="publishers" size={48}>Publishers</Tab>
      </TabList>
    </Tabs>
  );
}

const progressIndicatorStyles = stylex.create({
  base: {
    marginBottom: 20,
    marginTop: 56,
  }
});

const emptyStateStyles = stylex.create({
  base: {
    paddingTop: 8,
  },
  suggestions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
    paddingTop: 8,
  }
});

const tabsStyles = stylex.create({
  base: {
    borderBottomStyle: 'none',
    flexBasis: 'auto',
    flexGrow: 1,
    flexShrink: 1,
  },
  list: {
    marginLeft: 'auto',
    marginRight: 'auto',
    minWidth: 728,
  }
});
